import { useEffect, useState } from "react";
import type { Route } from "next";
import Link from "next/link";
import { Search, X } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useLayout } from "./context";

export function HeaderSearch() {
  const t = useTranslations("layout30");
  const locale = useLocale();
  const { settings } = useLayout();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchKey =
    settings.audience === "supplier" ? "supplierSearch" : "search";
  const normalized = query.trim().toLocaleLowerCase(locale);
  const items = settings.sectionMenu
    .flatMap((group) => group.children ?? [])
    .filter((item): item is typeof item & { path: string; title: string } =>
      Boolean(item.path && item.title),
    )
    .map((item) => ({
      label: t(item.title as Parameters<typeof t>[0]),
      path: item.path,
    }))
    .filter((item) =>
      item.label.toLocaleLowerCase(locale).includes(normalized),
    );

  useEffect(() => {
    const openSearch = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", openSearch);
    return () => window.removeEventListener("keydown", openSearch);
  }, []);

  return (
    <div
      data-slot="header-search"
      data-debug-source="components/layouts/layout-30/components/header-search.tsx"
      className="flex shrink-0 items-center"
    >
      <Popover
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) setQuery("");
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            aria-label={t(`${searchKey}.label` as Parameters<typeof t>[0])}
            className="size-8 justify-center border-zinc-700 bg-zinc-950 p-0 text-zinc-300 hover:bg-zinc-800 hover:text-white md:h-auto md:min-h-7 md:w-auto md:max-w-64 md:justify-start md:px-2.5 md:py-1"
          >
            <Search className="size-4" aria-hidden="true" />
            <span className="hidden min-w-0 truncate md:inline">
              {t(`${searchKey}.placeholder` as Parameters<typeof t>[0])}
            </span>
            <kbd className="hidden shrink-0 rounded border border-zinc-700 bg-black px-1.5 py-0.5 font-mono text-sm text-zinc-400 xl:inline-flex">
              {t(`${searchKey}.shortcut` as Parameters<typeof t>[0])}
            </kbd>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-[min(22rem,calc(100vw-1rem))] p-2"
        >
          <div className="relative">
            <Input
              autoFocus
              type="search"
              value={query}
              placeholder={t(
                `${searchKey}.placeholder` as Parameters<typeof t>[0],
              )}
              aria-label={t(`${searchKey}.label` as Parameters<typeof t>[0])}
              className="pe-9"
              onChange={(event) => setQuery(event.target.value)}
            />
            {query ? (
              <Button
                type="button"
                variant="ghost"
                mode="icon"
                aria-label={t(`${searchKey}.clear` as Parameters<typeof t>[0])}
                className="absolute end-1 top-1/2 size-7 -translate-y-1/2"
                onClick={() => setQuery("")}
              >
                <X className="size-4" aria-hidden="true" />
              </Button>
            ) : null}
          </div>
          <div className="mt-2 max-h-72 overflow-y-auto overscroll-contain">
            {items.length ? (
              items.map((item) => (
                <Link
                  key={item.path}
                  href={item.path as Route}
                  onClick={() => setOpen(false)}
                  className="flex min-w-0 items-center rounded-md px-3 py-2 text-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="min-w-0 break-words">{item.label}</span>
                </Link>
              ))
            ) : (
              <p className="p-3 text-sm text-muted-foreground">
                {t(`${searchKey}.noResults` as Parameters<typeof t>[0])}
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
